import React, { useState, useRef } from "react";
import { Box, TextField, Radio, FormControlLabel, Button, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import "./../NcdForm.css";

dayjs.extend(customParseFormat);

export const Oralphysical = ({ data, onChange }) => {
  const [selectedValue, setSelectedValue] = useState('');
  const wrapperRef = useRef();

  const assesDate = data?.assesDate || '';
  const oralLctns = data?.oralLctns || {
    leukoplakiaPatch: [],
    erythroplakiaPatch: [],
    ulcerPatch: [],
    growthPatch: [],
    lockedjawPatch: [],
    subfibrosisPatch: [],
    smokerspalatePatch: [],
    lichenPlanusPatch: []
  };

  const dots = Array.from({ length: 16 }, (_, i) => ({
    id: `dot${i + 1}`,
    value: `OP${i + 1}`,
  }));

  const handleRadioChange = (event) => {
    const id = event.target.id;
    if (selectedValue === id) {
      setSelectedValue('');
    } else {
      setSelectedValue(id);
    }
  };

  const getDotPatchLabels = (dotValue, selectedVal = '') => {
    const labelMap = {
      Leukoplakia: { key: 'leukoplakiaPatch', label: 'Leukoplakia' },
      Erythroplakia: { key: 'erythroplakiaPatch', label: 'Erythroplakia' },
      Ulcer: { key: 'ulcerPatch', label: 'Ulcer' },
      Growth: { key: 'growthPatch', label: 'Growth' },
      LockedJaw: { key: 'lockedjawPatch', label: 'Locked Jaw' },
      Submucousfibrosis: { key: 'subfibrosisPatch', label: 'Submucous fibrosis' },
      Smokerspalate: { key: 'smokerspalatePatch', label: 'Smokers palate' },
      LichenPlanus: { key: 'lichenPlanusPatch', label: 'Lichen Planus' }
    };

    if (selectedVal && labelMap[selectedVal]) {
      const { key, label } = labelMap[selectedVal];
      if (oralLctns[key]?.includes(dotValue)) {
        return [label];
      }
      return [];
    }

    const labels = [];
    for (const id in labelMap) {
      const { key, label } = labelMap[id];
      if (oralLctns[key]?.includes(dotValue)) {
        labels.push(label);
      }
    }
    return labels;
  };

  const handleDotClickOral = (dotValue, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const patchKeyMap = {
      Leukoplakia: 'leukoplakiaPatch',
      Erythroplakia: 'erythroplakiaPatch',
      Ulcer: 'ulcerPatch',
      Growth: 'growthPatch',
      LockedJaw: 'lockedjawPatch',
      Submucousfibrosis: 'subfibrosisPatch',
      Smokerspalate: 'smokerspalatePatch',
      LichenPlanus: 'lichenPlanusPatch',
    };

    const currentSymptom = selectedValue || 'Leukoplakia';
    if (!selectedValue) {
      setSelectedValue('Leukoplakia');
    }

    const targetPatchKey = patchKeyMap[currentSymptom];
    if (!targetPatchKey) return;

    let updatedOralLctns = { ...oralLctns };
    const currentList = updatedOralLctns[targetPatchKey] || [];

    if (currentList.includes(dotValue)) {
      updatedOralLctns[targetPatchKey] = currentList.filter(d => d !== dotValue);
    } else {
      updatedOralLctns[targetPatchKey] = [...currentList, dotValue];
    }

    onChange({
      ...data,
      oralLctns: updatedOralLctns
    });
  };

  const handleReset = (e) => {
    e.preventDefault();
    onChange({
      ...data,
      oralLctns: {
        leukoplakiaPatch: [],
        erythroplakiaPatch: [],
        ulcerPatch: [],
        growthPatch: [],
        lockedjawPatch: [],
        subfibrosisPatch: [],
        smokerspalatePatch: [],
        lichenPlanusPatch: []
      }
    });
  };

  return (
    <>
      <Box className="row" sx={{ mb: 3 }}>
        <Box className="date_cont" sx={{ mb: 2, display: 'flex', width: '100%' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Assessment Date"
              format="DD/MM/YYYY"
              value={assesDate ? dayjs(assesDate, 'DD-MM-YYYY') : null}
              onChange={(newValue) => onChange({ ...data, assesDate: newValue ? newValue.format('DD-MM-YYYY') : '' })}
              maxDate={dayjs()}
              disableFuture
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: { maxWidth: 300 },
                  error: !!(assesDate && dayjs(assesDate, 'DD-MM-YYYY').isValid() && dayjs(assesDate, 'DD-MM-YYYY').isAfter(dayjs(), 'day')),
                  helperText: assesDate && dayjs(assesDate, 'DD-MM-YYYY').isValid() && dayjs(assesDate, 'DD-MM-YYYY').isAfter(dayjs(), 'day') ? 'Assessment Date cannot be a future date' : ''
                }
              }}
            />
          </LocalizationProvider>
        </Box>

        {[
          { id: "Leukoplakia", label: "Leukoplakia", img: "/images/any_white_or_red_patch_in_mouth.png" },
          { id: "Erythroplakia", label: "Erythroplakia", img: "/images/any_white_or_red_patch_in_mouth.png" },
          { id: "Ulcer", label: "Ulcer", img: "/images/any_white_or_red_patch_in_mouth.png" },
          { id: "Growth", label: "Growth", img: "/images/ulceration.png" },
          { id: "LockedJaw", label: "Locked Jaw", img: "/images/locked-jaw.png" },
          { id: "Submucousfibrosis", label: "Submucous fibrosis", img: "/images/oral-screening.png" },
          { id: "Smokerspalate", label: "Smokers palate", img: "/images/any_white_or_red_patch_in_mouth.png" },
          { id: "LichenPlanus", label: "Lichen Planus", img: "/images/any_white_or_red_patch_in_mouth.png" },
        ].map(({ id, label, img }) => {
          const isSelected = selectedValue === id;
          return (
            <Box key={id} className="col-md-3 col-xs-6 col-6" sx={{ mb: 2 }}>
              <FormControlLabel
                className={`oralRadio d-flex align-items-center justify-content-center luco-lable-text mb-1 ${isSelected ? 'selected' : ''}`}
                sx={{
                  border: isSelected ? '2px solid #5c6bc0' : '1px solid #cfd8dc',
                  backgroundColor: isSelected ? '#9fa8da' : '#ffffff',
                  color: isSelected ? '#1c2d5a' : '#2c3e50',
                  borderRadius: '6px',
                  m: 0,
                  p: '8px 12px',
                  width: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    border: isSelected ? '2px solid #5c6bc0' : '1px solid #5c6bc0',
                    backgroundColor: isSelected ? '#9fa8da' : '#f5f7fa',
                  },
                  '& .MuiTypography-root': {
                    fontWeight: 700,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center'
                  }
                }}
                control={
                  <Radio
                    name="oralScreeningRadio"
                    id={id}
                    value={id}
                    onChange={handleRadioChange}
                    checked={isSelected}
                    sx={{ display: 'none' }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box 
                      sx={{ 
                        width: 36, 
                        height: 36, 
                        borderRadius: '50%', 
                        backgroundColor: '#cfd8dc', 
                        border: '2px solid #ffffff', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        marginRight: '12px',
                        flexShrink: 0,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}
                    >
                      <img 
                        src={img} 
                        alt="" 
                        style={{ 
                          width: 20, 
                          height: 20,
                          objectFit: 'contain'
                        }} 
                      />
                    </Box>
                    <span style={{ fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
                  </Box>
                }
              />
            </Box>
          );
        })}
      </Box>

      <div className="oral-cancer-wrapper mouthDemo">
        <div className="mouthOralLoc">
          <div className="mouthOralLocHeader" style={{ borderBottom: '1px solid #e0e0e0', padding: '10px 15px' }}>
            <h6>Showing findings</h6>
            <div className="rightFinding">
              <div className="reset">
                <Button variant="text" size="small" type="button" onClick={handleReset}>Reset</Button>
              </div>
            </div>
          </div>
          <Typography variant="subtitle1" sx={{ textAlign: 'center', my: 2, fontWeight: 500 }}>
            {selectedValue ? `Mark Location for: ${selectedValue}` : 'Select a symptom above to mark location'}
          </Typography>
          <div className="mouth-wrapper" ref={wrapperRef}>
            {dots.map(({ id, value }) => {
              const labels = getDotPatchLabels(value, selectedValue);
              const isActive = labels.length > 0;
              const label = labels.join(', ');
              const dotColor = isActive ? '#b7122b' : '';
              return (
                <div
                  key={id}
                  className={`dot ${id} ${isActive ? "selected" : ""}`}
                  style={{
                    backgroundColor: isActive ? dotColor : undefined
                  }}
                  onClick={(e) => handleDotClickOral(value, e)}
                >
                  {isActive && (
                    <div className="dot-popup">
                      <p>{label}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};
